import {
  ColorMediaShape,
  CustomMediaShape,
  GradientMediaShape,
  VideoMediaShape,
} from "./media.types";
import {
  Border,
  Contents,
  FontFamilyType,
  FontSize,
  FontWeight,
  Height,
  LetterSpacing,
  LineHeight,
  LinkToType,
  MaxWidth,
  NavbarContents,
  NavbarItem,
  NavbarPosition,
  Shadow,
  SizeWithAuto,
  SizeWithFull,
  Slug,
  Width,
  MobilePayload,
  TextAlign,
  WidthWithScreen,
} from "./util.types";
import { ReactNode } from "react";
import { PersistedNavbarInteractableId } from "./types";

export enum ComponentType {
  Document = "document",
  Page = "page",
  Box = "box",
  Button = "button",
  Text = "text",
  Image = "image",
  Navbar = "navbar",
}

export enum StyledElementTag {
  div = "div",
  button = "button",
  span = "span",
  nav = "nav",
  input = "input",
}

export interface ComponentElementProps<T> extends Record<string, any> {
  componentKey: string;
  component: T;
  parentComponent?: T;
  children?: ReactNode;
  className?: string; // last resort styling that doesn't persist to website
  isStatic?: boolean;
  htmlTag?: StyledElementTag;
  breakpointWidthOffset?: number;
  interactableClassName?: string;
}

export interface ComponentBase<T = Component> {
  staticClassName?: string;
  className: string;
  children: T[];
}

export type Component =
  | DocumentComponent
  | PageComponent
  | BoxComponent
  | ButtonComponent
  | TextComponent
  | ImageComponent
  | NavbarComponent;
export type ComponentWithValues =
  | ButtonComponent
  | TextComponent
  | ImageComponent
  | NavbarComponent;
export type ComponentsWithResponsiveness = BoxComponent | NavbarComponent;

export const COMPONENTS_THAT_CANNOT_HAVE_KIDS = [
  ComponentType.Document,
  ComponentType.Text,
  ComponentType.Image,
  ComponentType.Button,
  ComponentType.Navbar,
];

export const COMPONENTS_THAT_ARE_DRAGGABLE = [
  ComponentType.Box,
  ComponentType.Text,
  ComponentType.Image,
  ComponentType.Button,
];

export const DROPPABLE_TARGETS_MAP: Record<ComponentType, ComponentType[]> = {
  [ComponentType.Document]: [],
  [ComponentType.Page]: [],
  [ComponentType.Box]: COMPONENTS_THAT_ARE_DRAGGABLE,
  [ComponentType.Text]: COMPONENTS_THAT_ARE_DRAGGABLE,
  [ComponentType.Image]: COMPONENTS_THAT_ARE_DRAGGABLE,
  [ComponentType.Button]: COMPONENTS_THAT_ARE_DRAGGABLE,
  [ComponentType.Navbar]: [],
};

export const COMPONENTS_WITH_RESPONSIVENESS = [
  ComponentType.Box,
  ComponentType.Navbar,
  ComponentType.Image,
];

// Individual components
export const DOCUMENT_STATIC_CLASSNAME =
  "flex justify-start items-center flex-col min-h-screen min-w-min";
export const PAGE_STATIC_CLASSNAME = "w-full"; // use max-width to set actual width
export const BOX_STATIC_CLASSNAME = "overflow-hidden w-full"; // use max-width to set actual width
export const NAVBAR_STATIC_CLASSNAME = "min-w-min w-full flex flex-row top-0";

export const NAVBAR_ICON_STATIC_CLASSNAME =
  "bg-center bg-no-repeat bg-contain shrink-0 flex-shrink-0 h-full";
export const TEXT_STATIC_CLASSNAME =
  "max-w-[inherit] whitespace-pre-wrap break-words";
export const BUTTON_STATIC_CLASSNAME = "max-w-[inherit] whitespace-pre-wrap";

export interface DocumentComponent extends Omit<ComponentBase, "children"> {
  type: ComponentType.Document;
  key: string;
  slug: Slug;
  page: PageComponent;
  navbar?: NavbarComponent | null;
  persistedNavbarInteractableId?: PersistedNavbarInteractableId | null; // defined if navbar is persisted across all pages
  styles: {
    pt: SizeWithAuto;
    pr: SizeWithAuto;
    pb: SizeWithAuto;
    pl: SizeWithAuto;
    bg:
      | CustomMediaShape
      | GradientMediaShape
      | VideoMediaShape
      | ColorMediaShape;
  };
}

export interface PageComponent extends ComponentBase {
  type: ComponentType.Page;
  styles: {
    pt: SizeWithAuto;
    pr: SizeWithAuto;
    pb: SizeWithAuto;
    pl: SizeWithAuto;
    mt: SizeWithAuto;
    mr: SizeWithAuto;
    mb: SizeWithAuto;
    ml: SizeWithAuto;
    maxW: MaxWidth;
    bg:
      | CustomMediaShape
      | GradientMediaShape
      | VideoMediaShape
      | ColorMediaShape;
    contents: Contents;
  };
}

export interface BoxComponent extends ComponentBase {
  type: ComponentType.Box;
  styles: {
    pt: SizeWithAuto;
    pr: SizeWithAuto;
    pb: SizeWithAuto;
    pl: SizeWithAuto;
    mt: SizeWithAuto;
    mr: SizeWithAuto;
    mb: SizeWithAuto;
    ml: SizeWithAuto;
    maxW: WidthWithScreen;
    h: Height;
    bg:
      | CustomMediaShape
      | GradientMediaShape
      | VideoMediaShape
      | ColorMediaShape;
    shadow: Shadow;
    border: Border;
    contents: Contents;
  };
  mobile: MobilePayload<BoxComponent>;
}

export interface ButtonComponent extends Omit<ComponentBase, "children"> {
  type: ComponentType.Button;
  values: {
    to: LinkToType;
  };
  styles: {
    family: FontFamilyType;
    size: FontSize;
    weight: FontWeight;
    lineSpacing: LineHeight;
    letterSpacing: LetterSpacing;
    align: TextAlign;
    pt: SizeWithAuto;
    pr: SizeWithAuto;
    pb: SizeWithAuto;
    pl: SizeWithAuto;
    mt: SizeWithAuto;
    mr: SizeWithAuto;
    mb: SizeWithAuto;
    ml: SizeWithAuto;
    w: Width;
    h: Height;
    bg: ColorMediaShape;
    shadow: Shadow;
    border: Border;
    color: ColorMediaShape;
    hover: {
      bg: ColorMediaShape | null;
      shadow: Shadow | null;
      border: Border | null;
      color: ColorMediaShape;
    } | null;
  };
}

export interface TextComponent extends Omit<ComponentBase, "children"> {
  type: ComponentType.Text;
  values: {
    text: string;
  };
  styles: {
    family: FontFamilyType;
    size: FontSize;
    weight: FontWeight;
    lineSpacing: LineHeight;
    letterSpacing: LetterSpacing;
    align: TextAlign;
    pt: SizeWithAuto;
    pr: SizeWithAuto;
    pb: SizeWithAuto;
    pl: SizeWithAuto;
    mt: SizeWithAuto;
    mr: SizeWithAuto;
    mb: SizeWithAuto;
    ml: SizeWithAuto;
    color: ColorMediaShape;
    shadow: Shadow;
  };
}

export interface ImageComponent extends Omit<ComponentBase, "children"> {
  type: ComponentType.Image;
  values: {
    alt: string;
  };
  styles: {
    image: CustomMediaShape | VideoMediaShape | null;
    mt: SizeWithAuto;
    mr: SizeWithAuto;
    mb: SizeWithAuto;
    ml: SizeWithAuto;
    w: SizeWithFull;
    h: SizeWithFull;
    keepRatio: boolean;
    shadow: Shadow;
    border: Border;
  };
  mobile: MobilePayload<ImageComponent>;
}

export interface NavbarComponent extends Omit<ComponentBase, "children"> {
  type: ComponentType.Navbar;
  values: {
    items: NavbarItem[];
    persist: boolean;
  };
  styles: {
    pt: SizeWithAuto;
    pr: SizeWithAuto;
    pb: SizeWithAuto;
    pl: SizeWithAuto;
    mt: SizeWithAuto;
    mr: SizeWithAuto;
    mb: SizeWithAuto;
    ml: SizeWithAuto;
    maxW: MaxWidth;
    h: Height;
    bg:
      | CustomMediaShape
      | GradientMediaShape
      | VideoMediaShape
      | ColorMediaShape;
    shadow: Shadow;
    border: Border;
    contents: NavbarContents;
    position: NavbarPosition;
  };
  mobile: MobilePayload<NavbarComponent>;
}
